/**
 * CORE-371 (P7.3) — a single-channel client over Agape.ChatApi's SignalR hub
 * (`/hubs/chat`), used behind the `ai_chat_via_chat_api` flag to back the AI /
 * Wisdom Council chat instead of GetStream. It owns one hub connection, keeps a
 * local message map fed by the hub events, and exposes just what `useChatThread`
 * needs — history + live updates mapped to {@link ThreadMessage}, plus send /
 * react / typing / read.
 *
 * Hub contract (server: Agape.ChatApi/Hubs/ChatHub.cs):
 *   invoke  WatchChannel(key, limit) → MessagePageDto { items, hasMore }
 *   invoke  SendMessage(key, text, parentMessageId?, attachmentsJson?, pageContextJson?)
 *   invoke  SendReply(key, text, parentMessageId, replyToMessageId, attachmentsJson, pageContextJson)
 *           (CORE-1683 — all six positional, null where unused)
 *   invoke  SendMessageV2(key, { text, parentMessageId, replyToMessageId, attachmentIds,
 *           clientMessageId, pageContextJson }) (CORE-1682 — server-stored uploads;
 *           CORE-1749 — also `replyToFragment` / `attachmentsJson`; CORE-1791 —
 *           this client sends everything through it, for the retry key)
 *   invoke  AddReaction/RemoveReaction(messageId, type)
 *   invoke  DeleteMessage(messageId) · EditMessage(messageId, text)
 *   invoke  Typing(key) · MarkRead(key, lastReadMessageId?)
 *   event   MessageNew/MessageUpdated(MessageDto) · MessageDeleted{ messageId }
 *   event   ReactionChanged{ messageId, reactions } · Typing{ userId }
 *   event   ReadReceipt{ userId, lastReadMessageId } · AiIndicator{ state }
 *
 * Streaming AI replies arrive as a MessageNew followed by MessageUpdated events
 * (token-by-token) — the map upsert handles both. The companion "thinking"
 * indicator is the `AiIndicator` event (state `update` on, `clear` off).
 */
import * as signalR from '@microsoft/signalr';
import { hubLogger } from '../../utils/hubConnectionOptions';
import { withHubTicket } from '../../utils/hubTicket';
import { storage } from '../../utils/storage';
import { CHAT_HUB_BASE_URL, chatHubRuProxy } from '../../constants/chat';
import { isChatRuActive, resolveHubUrl } from '../apiEndpoint';
import { ThreadMessage } from './chatTypes';
import { ChatThreadSource } from './chatThreadSource';
import { api } from '../api';
import { isDeletedChatApiMessage, mapChatApiMessages, myReactionsOf, parseChatApiReactionChanged, reactionsAfterChanged, ChatApiMessageDto } from './chatApiMessageMapper';
import { DeadlineExceededError, withDeadline } from './withDeadline';
import { isHubRefusal } from './hubRefusal';
import {
  _resetHubPathForTest,
  chooseHubBase,
  clearHubPathStalled,
  clearHubProxySticky,
  markHubPathStalled,
  markHubProxyWorking,
} from './hubPath';
import { isAiCompanionChannelKey } from './channelResolver';
import {
  classifyHubDrop, describeHubTransport, lastKnownTransport, readDropVisibility,
  type HubTransport,
} from '../../utils/hubDropContext';
import { getCurrentPageContext } from './pageContext';
import { notifyUnreadChanged } from './unreadSignal';
import { analytics } from '../analytics';
import { advanceHubLifecycle, initialHubLifecycleState } from '../../utils/hubLifecycleTelemetry';
import type { HubConnState, HubLifecycleTrackerState } from '../../utils/hubLifecycleTelemetry';
import { startHubWithTimeout } from '../../utils/hubStartWithTimeout';
import { newClientMessageId } from './sendAttempt';

// CORE-407 — how long to wait for the direct hub socket before falling back to
// the RU proxy. RU ISPs that block the hub let TCP connect but hang the WS, so a
// timeout (not just an error) is what triggers the fallback.
const HUB_CONNECT_TIMEOUT_MS = 10_000;
// CORE-1020 — выбор пути и память о его состоянии переехали в `hubPath`: тот
// же путь использует и сокет присутствия, и «этот путь встал» — свойство пути,
// а не сокета, который заметил это первым.

// CORE — how long to tolerate server silence before declaring a timeout. The
// SignalR default is 30 s, and through the RU edge (chat-ru.*) keepalives do not
// reliably land inside it: the client then kills a perfectly healthy socket with
// «Server timeout elapsed without receiving a message from the server», loses
// every MessageNew until it reconnects, and truncates a streaming AI reply
// mid-word. useRoomHub already widened this for the same reason.
const HUB_SERVER_TIMEOUT_MS = 60_000;
const HUB_KEEPALIVE_MS = 15_000;

/**
 * CORE-1020 — сколько ждать ответа на `WatchChannel`, прежде чем считать канал
 * неоткрывшимся.
 *
 * Приём тот же, что у `startWithTimeout` ниже, и по той же причине — только
 * прежде его поставили лишь на `start()`. Между тем сокет и вызов по нему рвутся
 * порознь: на дев-эдже для РФ рукопожатие (десятки байт) проходит, а туннель
 * встаёт через несколько обменов и молчит — `start()` доволен, `WatchChannel`
 * не отвечает никогда. Незавершающийся `await` проходит мимо всей обвязки:
 * повторов нет, ошибки нет, `onclose` не срабатывает (сокет-то «живой»), и
 * `scheduleRestart` не запускается. Снаружи это ровно то, о чём заведён тикет:
 * тред открылся, история не пришла, ответа нет.
 *
 * 8 секунд: здоровый `WatchChannel` отвечает за ~0.3 с. Меньше 10 с нужно и
 * арифметически — верхний дедлайн одной попытки в `useChatThreadSource`
 * составляет 20 с, а внутри неё должны поместиться прямая попытка и повтор
 * через РФ-прокси.
 */
const WATCH_TIMEOUT_MS = 8_000;

/**
 * CORE-1020 (прод, 02.09) — сколько ждать подтверждения отправки.
 *
 * Отчёт: сообщение уходит из поля ввода, в чате не появляется, ответа нет, а
 * после перезагрузки страницы иногда появляются и сообщение, и ответ. Замер по
 * живому аккаунту на `app-ru.agapella.io`: сообщения и ответы Agapella лежат на
 * сервере — то есть отправка дошла, ответ сгенерирован, сломана доставка
 * обратно. Через РФ-эдж (`chat-ru.agapella.io`) туннель встаёт после пяти-шести
 * обменов и молчит; напрямую те же двадцать обменов проходят без запинки.
 *
 * `SendMessage` дедлайна не имел, поэтому на вставшем туннеле обещание не
 * завершалось никогда: поле ввода очищено, сообщения нет, ошибки нет, и сокет
 * при этом «подключён». Возврата отсюда не было вовсе — только перезагрузка.
 *
 * 12 секунд: сама отправка возвращает созданное сообщение сразу, ответ
 * Agapella приходит потом отдельным событием и этого ожидания не держит.
 */
const SEND_TIMEOUT_MS = 12_000;

/**
 * CORE-1020 — потолок для остальных вызовов хаба: реакция, правка, удаление,
 * отметка прочитанного. Все они короткие и отвечают за доли секунды; 8 секунд
 * здесь означают «сокет мёртв», а не «сервер думает».
 */
const HUB_INVOKE_TIMEOUT_MS = 8_000;

/**
 * Сколько ждать уже идущего переподключения, прежде чем сказать «нет связи».
 *
 * Отчёт с прода: «Сообщение не отправилось. Нет связи с чатом». Связи в тот
 * момент действительно нет — но она возвращается сама, и обычно за секунду:
 * первая ступень `withAutomaticReconnect` это 0–1 с, политика не сдаётся
 * никогда, а терминальный обрыв поднимает `scheduleRestart` с тем же началом.
 *
 * На телефоне окно без связи открывается постоянно: блокировка экрана, переход
 * с Wi-Fi на сотовую, сворачивание приложения. Человек в это окно жмёт
 * «отправить» — и получает окончательный отказ по условию, которое лечится
 * само через секунду.
 *
 * Асимметрия и была дефектом: ради восстановления связи здесь есть целый
 * модуль отступов с разбросом, запасной РФ-путь и ловля вставшего туннеля, — а
 * отправка не ждала ничего из этого ни секунды.
 *
 * Пять секунд: покрывает первую ступень и первый перезапуск с запасом, и при
 * этом не превращает нажатие в подвисший экран. Не дождались — отказ прежний,
 * с сохранённым текстом.
 */
const RECONNECT_GRACE_MS = 5_000;

/** Как часто заглядывать в состояние сокета, пока ждём. */
const RECONNECT_POLL_MS = 100;

/** Reconnect backoff: 1s, 2s, 4s … capped at 30 s, and never gives up. The
 *  default policy stops after four tries (~42 s), after which the channel is
 *  dead until a page reload — which is what «messages only arrive after F5»
 *  looked like. */
/** Только для тестов: флаги выбора пути общие, см. `hubPath`. */
export function _resetHubPathFlagsForTest(): void {
  _resetHubPathForTest();
}

export function retryDelayMs(previousRetryCount: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.min(previousRetryCount, 5));
}

/** How many times to re-join the channel group after an auto-reconnect before
 *  giving up on the socket and rebuilding it instead. Four attempts on the
 *  backoff above spans ~15 s — long enough to ride out a server still coming
 *  back up (the moment reconnects arrive in bulk), short enough that a truly
 *  broken connection is replaced rather than left silently outside its group. */
const RESUBSCRIBE_ATTEMPTS = 4;

export class ChatApiChannel implements ChatThreadSource {
  readonly channelKey: string;
  private readonly myId: string;
  private conn: signalR.HubConnection | null = null;
  /** Текущее соединение поднято через РФ-прокси, а не прямым адресом. */
  private usingProxy = false;
  private readonly messages = new Map<string, ChatApiMessageDto>();
  /**
   * My own reactions per message: the slugs set (empty when I took them all
   * off). Absent = no local opinion, fall back to the server's `mine` flag.
   *
   * Needed because `ReactionChanged` is a broadcast: every member receives the
   * SAME payload, so it cannot carry a per-user `mine`, and the handler replaces
   * the stored reaction list with it. After the first reaction the flag was gone,
   * `myReaction` read as null, and tapping my own emoji again sent AddReaction
   * instead of RemoveReaction — the reaction could be set but never removed.
   */
  private readonly myReactions = new Map<string, readonly string[]>();
  private othersLastReadId = 0;
  private aiThinking = false;
  // CORE-356 — a peer is typing in a DM/circle (distinct from AI "thinking",
  // which rides AiIndicator). Set by the `Typing` hub event, auto-cleared.
  private peerTyping = false;
  private peerTypingTimer: ReturnType<typeof setTimeout> | null = null;
  private changeCb: (() => void) | null = null;
  private destroyed = false;
  // Track the in-flight start so disconnect() can wait for it before stop() —
  // stopping mid-start logs "Failed to start the HttpConnection before stop()
  // was called" (benign, but noisy when quickly switching channels).
  private startPromise: Promise<void> | null = null;
  /**
   * CORE-1211 — хаб отказал по существу: канал приватный и членства нет, канал
   * не существует, в токене нет идентификатора.
   *
   * Флаг живёт на экземпляре и не снимается: право читать этот канал у этого
   * человека сейчас отсутствует, и ни пауза, ни другой путь до сервера этого не
   * изменят. Когда доступ появится (человек подписался на круг, бан истёк),
   * экран пересоберёт канал заново — это будет уже другой экземпляр.
   */
  private refused = false;
  /** CORE-1683 — курсор страницы старше загруженной истории и есть ли она. */
  private olderCursor: string | null = null;
  private hasOlder = false;
  // Self-healing restart after the connection closed for good (see onclose).
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private restartAttempt = 0;
  // Pending re-join of the channel group after an auto-reconnect (resubscribe).
  private resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
  // Lifecycle telemetry. `connState` is deliberately "connected" only once
  // WatchChannel has landed: a socket that is open but outside the channel
  // group delivers nothing, so counting it as connected would hide exactly the
  // outage we are trying to measure.
  private connState: HubConnState = 'disconnected';

  /** CORE-1194 — чем оборвалось последнее соединение; читает и снимает
   *  `setConnState`, когда описывает этот разрыв в телеметрии. */
  private dropError: unknown = null;

  /** Прочитать ошибку разрыва и снять её: она описывает ровно один разрыв. */
  /** CORE-1194 — транспорт живого соединения; к разрыву SignalR его уже снял. */
  private lastTransport: HubTransport = 'unknown';

  private takeDropError(): unknown {
    const err = this.dropError;
    this.dropError = null;
    return err;
  }
  private lifecycle: HubLifecycleTrackerState = initialHubLifecycleState();

  constructor(channelKey: string, myId: string) {
    this.channelKey = channelKey;
    this.myId = myId;
  }

  /** Register the single change listener (replaces any previous). */
  onChange(cb: () => void) {
    this.changeCb = cb;
  }

  private emit() {
    if (!this.destroyed) this.changeCb?.();
  }

  private idOf(dto: any): string {
    return String(dto?.id ?? dto?.Id ?? '');
  }

  private upsert(dto: any) {
    const id = this.idOf(dto);
    if (id) this.messages.set(id, dto);
  }

  /** Build (but don't start) a hub connection to `hubBase` with every event
   *  handler wired. Split out so connect() can rebuild against the RU proxy. */
  private buildConn(hubBase: string): signalR.HubConnection {
    const conn = new signalR.HubConnectionBuilder()
      // CORE-413 env-switch + CORE-45 RU-first applied to the hub URL.
      .withUrl(resolveHubUrl(`${hubBase}/hubs/chat`), {
        accessTokenFactory: withHubTicket(() => storage.getToken()), // CORE-1736
        logger: hubLogger, // CORE-1364 — no JWT in the console
      })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (ctx) => retryDelayMs(ctx.previousRetryCount) })
      .build();

    conn.serverTimeoutInMilliseconds = HUB_SERVER_TIMEOUT_MS;
    conn.keepAliveIntervalInMilliseconds = HUB_KEEPALIVE_MS;

    conn.on('MessageNew', (dto: any) => {
      this.upsert(dto);
      this.emit();
    });
    conn.on('MessageUpdated', (dto: any) => {
      this.upsert(dto);
      this.emit();
    });
    conn.on('MessageDeleted', (p: any) => {
      const id = String(p?.messageId ?? p?.MessageId ?? '');
      if (id && this.messages.delete(id)) this.emit();
    });
    conn.on('ReactionChanged', (p: any) => {
      const id = String(p?.messageId ?? p?.MessageId ?? '');
      const existing = id ? this.messages.get(id) : undefined;
      if (existing) {
        const reactions = p?.reactions ?? p?.Reactions;
        if (reactions !== undefined) {
          // CORE-1685 (QA george 29.10.2026): the frame's `mine` is the actor's and goes to every
          // member — never stored. Mine is what this client knew, corrected by a frame about me.
          const prev = existing.reactions ?? existing.Reactions;
          const next = reactionsAfterChanged(reactions, prev, this.myReactions.get(id), parseChatApiReactionChanged(p), this.myId);
          existing.reactions = next.reactions;
          existing.Reactions = next.reactions;
          // A frame about me (another tab or device of mine) is as good as my own call.
          if (next.aboutMe) this.myReactions.set(id, next.mine);
        }
        this.emit();
      }
    });
    conn.on('AiIndicator', (p: any) => {
      this.aiThinking = (p?.state ?? p?.State) === 'update';
      this.emit();
    });
    // Peer typing (DM/circle). AI companions never emit Typing (they use
    // AiIndicator), so this is inert on AI channels. Auto-clears after 4s in
    // case a stop signal is missed.
    conn.on('Typing', (p: any) => {
      const uid = String(p?.userId ?? p?.UserId ?? '');
      if (!uid || uid === this.myId) return;
      this.peerTyping = true;
      if (this.peerTypingTimer) clearTimeout(this.peerTypingTimer);
      this.peerTypingTimer = setTimeout(() => {
        this.peerTyping = false;
        this.emit();
      }, 4000);
      this.emit();
    });
    conn.on('PresenceChanged', () => {});
    conn.on('ReadReceipt', (p: any) => {
      const uid = p?.userId ?? p?.UserId;
      const lr = Number(p?.lastReadMessageId ?? p?.LastReadMessageId ?? 0);
      if (uid && uid !== this.myId && lr > this.othersLastReadId) {
        this.othersLastReadId = lr;
        this.emit();
      }
    });
    // «Удалить у всех» from the other side (agape-backend 7cf9bf3) — the DM was
    // hard-truncated for both, so drop the local history live without a manual
    // refresh. A new message re-opens the conversation as usual.
    conn.on('ConversationCleared', () => {
      this.messages.clear();
      this.othersLastReadId = 0;
      this.emit();
    });

    // CORE-1194 — ошибка разрыва живёт до ближайшего события телеметрии и
    // снимается им же: она описывает ровно один разрыв.
    // CORE-1494 — события жизненного цикла приходят и от соединений, которые
    // канал уже бросил (проваленная попытка, сменённый путь). Обработчики ниже
    // работают с `this`, то есть с ТЕКУЩИМ соединением: переподписка чужого
    // сокета остановила бы не тот, `onclose` чужого запустил бы лишний
    // перезапуск. Брошенное соединение просто гасим.
    const stale = () => {
      if (conn === this.conn) return false;
      void conn.stop().catch(() => {});
      return true;
    };

    conn.onreconnecting((err) => {
      if (stale()) return;
      this.dropError = err ?? null;
      this.setConnState('reconnecting');
    });

    // On reconnect the socket missed events while down — re-pull history. This
    // is also what repairs a streaming AI reply that was cut off mid-word: the
    // server holds the finalized text.
    //
    // It is also the ONLY thing that puts us back in the channel's SignalR
    // group: membership is keyed on connection id (ChatHub.WatchChannel →
    // Groups.AddToGroupAsync), so an auto-reconnect silently leaves the group
    // behind. Until WatchChannel lands again this socket is open, healthy, and
    // receives no MessageNew / MessageUpdated / AiIndicator at all.
    conn.onreconnected(() => {
      if (stale()) return;
      this.setConnState('connecting');
      void this.resubscribe();
    });

    // Last resort: the retry policy above never gives up, but a terminal error
    // (expired token, hub gone) still closes the connection for good. Without
    // this the channel stays silent until the user reloads the page.
    conn.onclose((err) => {
      if (this.destroyed || stale()) return;
      this.dropError = err ?? null;
      this.setConnState('disconnected');
      this.scheduleRestart();
    });
    return conn;
  }

  /**
   * Re-join the channel group after the SDK auto-reconnected.
   *
   * This used to be one call plus a single 1.5 s retry, then `catch(() => {})`.
   * That silence was a dead end with no way out: the socket is healthy, so
   * `onclose` never fires and `scheduleRestart` never runs, yet the connection
   * belongs to no group. The tab shows a live connection and receives nothing —
   * the user writes, the companion answers, the server persists and broadcasts
   * to a group we are not in, and only a page reload brings the message back.
   *
   * So: retry on the same backoff the connection itself uses, and when the
   * attempts run out, stop pretending the socket is useful and rebuild it. A
   * rebuilt connection re-watches from `connect()`, which is the one path that
   * cannot end up connected-but-unsubscribed.
   */
  private async resubscribe(attempt = 0): Promise<void> {
    if (this.destroyed) return;
    try {
      await this.watch();
      this.setConnState('connected');
      return;
    } catch {
      if (this.destroyed) return;
    }
    // CORE-1211 — доступ отозвали, пока мы были на связи: человека забанили в
    // круге, он сам из него вышел, канал удалили. Лестница повторов рассчитана
    // на сервер, который ещё поднимается после перезапуска, и к этому случаю
    // отношения не имеет: четыре попытки, потом снос сокета и вечный перезапуск
    // — всё ради ответа, который не изменится. Сокет рвём (в группе его всё
    // равно нет), но больше не стучимся.
    if (this.refused) {
      this.setConnState('disconnected');
      const closed = this.conn;
      this.conn = null;
      await closed?.stop().catch(() => {});
      return;
    }
    if (attempt + 1 >= RESUBSCRIBE_ATTEMPTS) {
      this.setConnState('disconnected');
      // Tear the socket down ourselves — nothing else will, and a connection
      // outside its group is worse than no connection: it looks alive.
      const c = this.conn;
      this.conn = null;
      await c?.stop().catch(() => {});
      this.scheduleRestart();
      return;
    }
    this.resubscribeTimer = setTimeout(() => {
      this.resubscribeTimer = null;
      void this.resubscribe(attempt + 1);
    }, retryDelayMs(attempt));
  }

  /** Feed one connection-state transition to the lifecycle reducer and emit
   *  whatever it decides is worth recording. */
  private setConnState(next: HubConnState): void {
    const prev = this.connState;
    if (next === prev) return;
    this.connState = next;
    if (next === 'connected') {
      const transport = describeHubTransport(this.conn);
      if (transport !== 'unknown') this.lastTransport = transport;
    }
    const { state, event } = advanceHubLifecycle(
      this.lifecycle, prev, next, Date.now(),
    );
    this.lifecycle = state;
    if (!event) return;
    analytics.track('chat_hub_lifecycle', {
      channelKey: this.channelKey,
      isCompanion: isAiCompanionChannelKey(this.channelKey),
      ...event,
      // CORE-1194 — тот же контекст разрыва, что у комнаты. Оба хаба рвутся
      // одинаково ровно, и различать причины по одному из них — значит
      // половину картины выбросить.
      ...(event.phase === 'disconnected'
        ? {
          cause: classifyHubDrop(this.takeDropError()),
          transport: lastKnownTransport(describeHubTransport(this.conn), this.lastTransport),
          ...readDropVisibility(),
        }
        : {}),
    });
  }

  /** Rebuild the connection after it closed for good, with capped backoff. */
  private scheduleRestart(): void {
    // CORE-1211 — перезапуск лечит обрыв, а не отказ в правах. Без этой строки
    // цикл вечен: `connect()` → `WatchChannel` → `forbidden` → `catch` →
    // `scheduleRestart` → и так каждые 30 секунд, пока экран открыт. Именно он
    // и дал 2356 событий в Sentry — сервер отвечал одно и то же, клиент спрашивал
    // снова.
    if (this.destroyed || this.refused || this.restartTimer) return;
    const delay = retryDelayMs(this.restartAttempt);
    this.restartAttempt += 1;
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.destroyed) return;
      this.connect()
        .then(() => { this.restartAttempt = 0; })
        .catch(() => this.scheduleRestart());
    }, delay);
  }

  /**
   * CORE-1020 — открыть канал, и чем бы попытка ни кончилась, оставить после
   * себя честное состояние.
   *
   * Раньше отсюда вело три отказных выхода — отказ хаба в правах, отсутствие
   * запасного пути и провал второй попытки через прокси, — и ни один не
   * возвращал состояние обратно. `connecting` ставился в начале и при неудаче
   * так и оставался: канал, который НЕ открылся, до конца жизни числился
   * открывающимся.
   *
   * Стоило это не аккуратности, а счёта попыток. `setConnState` ничего не
   * делает при переходе в то же самое состояние, а `scheduleRestart` начинает
   * каждую следующую попытку с `connecting`. Значит после первого же неудачного
   * захода все последующие были для телеметрии переходом «из connecting в
   * connecting», то есть ничем. `advanceHubLifecycle` наращивает `attempts`
   * ровно на переходе в `connecting` при открытом срыве — и переставал их
   * видеть. Событие `reconnected` потом честно сообщало «восстановились с
   * первой попытки», сколько бы их ни было на самом деле; «номер попытки», ради
   * которого поле и заводили (CORE-897), врал тем сильнее, чем дольше рвалось.
   *
   * Отдельного события за провал открытия здесь не появляется, и это намеренно:
   * редьюсер открывает срыв только от живого соединения. Канал, не открывшийся
   * ни разу, по-прежнему молчит — чинится счёт, а не правила.
   *
   * Поэтому тело вынесено целиком, а здесь остался один узел, через который
   * проходит любой исход. Ошибка идёт дальше нетронутой — её разбирают выше
   * (`useChatThreadSource` отличает отказ от обрыва); задача этого места не
   * подменить её, а не дать провалу пройти бесследно.
   */
  async connect(): Promise<void> {
    try {
      await this.openConnection();
    } catch (err) {
      this.setConnState('disconnected');
      throw err;
    }
  }

  private async openConnection(): Promise<void> {
    this.setConnState('connecting');
    const primary = CHAT_HUB_BASE_URL;
    // Resolved per connect, not per module load — see chatHubRuProxy().
    const proxy = chatHubRuProxy();
    // RU-first (CORE-45): if the startup probe picked the RU edge, connect to the
    // proxy DIRECTLY — same decision that routes REST/hubs there, so RU users skip
    // the direct-hub connect-timeout. Правило целиком — в `hubPath`.
    const { base, usingProxy, canFallback } = chooseHubBase(primary, proxy, isChatRuActive());
    this.usingProxy = usingProxy;

    const first = this.buildConn(base);
    this.conn = first;
    try {
      this.startPromise = this.startWithTimeout(first, HUB_CONNECT_TIMEOUT_MS);
      await this.startPromise;
      // CORE-1020 — `WatchChannel` внутри той же попытки, а не после неё.
      // Открытым сокет считается не тогда, когда поднялся, а когда по нему
      // пришла история: связь, которая душит туннель, рукопожатие пропускает, а
      // первый содержательный обмен — уже нет. Пока вызов стоял снаружи,
      // единственный запасной путь, заведённый ровно на этот случай, при таком
      // отказе не пробовался ни разу.
      await this.watch();
    } catch (err) {
      // Пометку о вставшем пути ставит сам `watch()` — там же, где виден отказ.
      // Провал `start()` пометкой не считается и не должен: это «сюда не
      // пускают», ради таких случаев прокси и заведён.
      // RU ISPs hang the direct hub socket → chat stuck on "Opening conversation…".
      // Retry once via the RU proxy if one is configured (and not already used).
      //
      // CORE-1211 — но не тогда, когда хаб уже ответил «нельзя». Запасной путь
      // ведёт к тому же серверу и той же проверке прав, так что второй заход
      // отличается от первого только адресом и вторым событием в Sentry: каждое
      // открытие недоступного канала стоило двух отказов вместо одного.
      //
      // Сокет при этом закрываем сами: читать по нему нечего, а звать
      // `disconnect()` после неудачного открытия догадывается не каждый хозяин
      // экрана — и открытое соединение оставалось висеть до ухода со страницы.
      //
      // CORE-1494 — и так при ЛЮБОМ провале попытки, а не только перед заходом
      // через прокси. Отказ по таймауту не останавливает сам `start()`: SignalR
      // продолжает подключаться, и на медленном канале поднимается уже после
      // того, как мы сдались. Такой сокет никому не принадлежит — `disconnect()`
      // закрывает только `this.conn`, — а автопереподключение у него вечное.
      // Вкладка из РФ-эджа за часы копила их десятками, каждый со своими
      // пингами и переподключениями.
      await this.dropConn(first);
      if (this.refused || !canFallback || base === proxy || this.destroyed) throw err;
      const second = this.buildConn(proxy);
      this.conn = second;
      this.usingProxy = true;
      try {
        this.startPromise = this.startWithTimeout(second, HUB_CONNECT_TIMEOUT_MS);
        await this.startPromise;
        await this.watch();
      } catch (proxyErr) {
        await this.dropConn(second);
        throw proxyErr;
      }
      // Липкость ставится только после истории: сокет, поднявшийся и молчащий,
      // рабочим путём не является, и уводить на него остальные каналы нельзя.
      markHubProxyWorking();
    }
    this.setConnState('connected');
  }

  /** conn.start() but reject after `ms` — a hung RU WS never rejects on its own,
   *  so without this the thread blocks on "Opening conversation…" forever.
   *
   *  CORE-1202 — гонка переехала в `startHubWithTimeout`: местная копия
   *  оставляла проигравший `conn.start()` без подписчика, и его отмена нашим же
   *  `stop()` уходила в `onunhandledrejection` вместо строчки с адресом. */
  /** CORE-1494 — закрыть соединение проваленной попытки, даже если его
   *  `start()` ещё идёт (SignalR тогда прерывает подключение). `this.conn`
   *  снимается, только если это всё ещё оно. */
  private async dropConn(conn: signalR.HubConnection): Promise<void> {
    if (this.conn === conn) this.conn = null;
    await conn.stop().catch(() => {});
  }

  private startWithTimeout(conn: signalR.HubConnection, ms: number): Promise<void> {
    return startHubWithTimeout(conn, ms, 'ChatApi hub', (err) => {
      analytics.track('chat_api_hub_start_aborted', {
        channelKey: this.channelKey,
        reason: err instanceof Error ? err.message : String(err),
      });
    });
  }

  private async watch(): Promise<void> {
    if (!this.conn) return;
    let page: any;
    try {
      page = await withDeadline(
        this.conn.invoke('WatchChannel', this.channelKey, 50),
        WATCH_TIMEOUT_MS,
        'WatchChannel',
      );
    } catch (err) {
      // CORE-1020 — пометка о вставшем пути стоит ЗДЕСЬ, а не у вызывающих.
      //
      // `watch()` зовут из трёх мест: открытие канала, повторная подписка после
      // автопереподключения и добор истории после отправки. Отмечать путь у
      // каждого значило бы три одинаковых оговорки и четвёртую забытую при
      // следующем вызове — так уже и вышло: сперва пометка была только у
      // отправки, потом её добавили открытию, а повторная подписка осталась без
      // неё и возвращала человека на тот же вставший путь.
      //
      // Условие то же самое: сокет ПОДНЯЛСЯ (иначе мы бы сюда не дошли), а
      // история не пришла. Это вставший туннель, а не «сюда не пускают».
      //
      // CORE-1211 — кроме одного случая: хаб ОТВЕТИЛ, и ответ был «нельзя».
      // Путь при этом отработал безупречно — по нему пришёл отказ, — и метить
      // его вставшим значит наказывать связь за решение о правах. Пометка
      // общая с сокетом присутствия, так что один недоступный канал уводил с
      // РФ-прокси весь чат. CORE-1494 — «до конца сессии» это больше не значит:
      // пометка истекает по сроку и снимается успехом того же пути, — но
      // отказ в правах ставить её по-прежнему не должен.
      if (isHubRefusal(err)) this.refused = true;
      else this.markPathStalled();
      throw err;
    }
    // CORE-1494 — история пришла, значит путь качает байты: снимаем пометку о
    // его срыве. Пометка модульная и общая с сокетом присутствия, поэтому
    // держать её после доказательства работоспособности нельзя — одна разовая
    // неудача (пропал wifi, перезапустился стенд) иначе до конца сессии уводила
    // весь чат на противоположный путь. Снимается только пометка ЭТОГО пути:
    // память о другом сорвавшемся нужна и дальше.
    clearHubPathStalled(this.usingProxy);
    const items: any[] = page?.items ?? page?.Items ?? [];
    this.messages.clear();
    // A full history pull is answered per-caller, so its `mine` flags are the
    // authority again — drop the local overrides rather than let them shadow it.
    this.myReactions.clear();
    items.forEach((d) => this.upsert(d));
    // CORE-1683 — откуда листать старше: курсор страницы, иначе самый старый id.
    const cursor = page?.nextCursor ?? page?.NextCursor;
    this.olderCursor = cursor == null || cursor === '' ? this.oldestId() : String(cursor);
    const hasMore = page?.hasMore ?? page?.HasMore;
    // Хаб без флага (старая сборка): полная страница — значит, старше ещё есть.
    this.hasOlder = typeof hasMore === 'boolean' ? hasMore : items.length >= 50;
    this.emit();
  }

  /** Самый старый из загруженных id (ключи — числовые id сообщений). */
  private oldestId(): string | null {
    let min: number | null = null;
    for (const key of this.messages.keys()) {
      const n = Number(key);
      if (Number.isFinite(n) && (min == null || n < min)) min = n;
    }
    return min == null ? null : String(min);
  }

  /**
   * CORE-1683 (QA 16.10) — страница истории старше загруженной. `WatchChannel`
   * отдаёт только новейшие 50 всего канала, и обсуждение, в котором накопилось
   * больше, теряло корень (карточку) и старые реплики: панель видела пустой
   * тред и глушила композер. Страница сливается по id, живые события
   * продолжают прилетать.
   *
   * CORE-620 (возврат QA 30.10) — упавший запрос бросает, а не возвращает
   * `false`: пустая страница при сбое читалась как конец истории, `hasOlder`
   * гас, и поиск карточки говорил «обсуждения нет» до перезагрузки. Курсор и
   * `hasOlder` при сбое не трогаем — повтор пойдёт с того же места.
   */
  async loadOlder(): Promise<boolean> {
    if (!this.hasOlder) return false;
    const cursor = this.olderCursor ?? this.oldestId();
    if (cursor == null) return false;
    const page = await api.getChatChannelMessagesPage(this.channelKey, { before: cursor });
    if (page.failed) throw new Error(`history page failed: ${this.channelKey}`);
    this.olderCursor = page.nextCursor ?? (page.items.length ? this.oldestId() : null);
    this.hasOlder = page.hasMore && page.items.length > 0;
    if (!page.items.length) return false;
    page.items.forEach((d) => this.upsert(d));
    this.emit();
    return true;
  }

  async resolveThreadRoot(messageId: string): Promise<string | null> {
    const page = await api.getChatChannelMessagesPage(this.channelKey, { around: messageId, limit: 1 });
    return page.threadRootId;
  }

  /**
   * CORE-1683 — обсуждение целиком по корню: сам корень с соседями, затем
   * страницы новее до конца. Ограничено числом страниц: тред на тысячи
   * сообщений листается дальше обычным путём.
   *
   * CORE-620 (возврат QA 30.10) — корень приходит первой страницей: упала она —
   * про корень ничего не известно, это сбой, а не «корня нет». Упавшая страница
   * новее обрывает добор ответов, как и раньше: про корень ответ уже есть.
   */
  async loadThread(rootId: string): Promise<boolean> {
    const first = await api.getChatChannelMessagesPage(this.channelKey, { around: rootId, threadRootId: rootId, limit: 100 });
    if (first.failed) throw new Error(`thread page failed: ${this.channelKey}`);
    if (!first.items.length) return false;
    first.items.forEach((d) => this.upsert(d));
    let cursor = first.hasNewer ? first.newerCursor : null;
    for (let i = 0; cursor != null && i < 10; i++) {
      const page = await api.getChatChannelMessagesPage(this.channelKey, { after: cursor, threadRootId: rootId, limit: 100 });
      page.items.forEach((d) => this.upsert(d));
      cursor = page.hasNewer && page.items.length ? page.newerCursor : null;
    }
    this.emit();
    return this.messages.has(rootId);
  }

  /** Current messages, oldest→newest, mapped for the UI. */
  getMessages(): ThreadMessage[] {
    return mapChatApiMessages(
      [...this.messages.values()],
      this.myId,
      this.othersLastReadId,
      this.myReactions,
    );
  }

  /** Whether a companion is currently "thinking" (drives the typing indicator). */
  isAiThinking(): boolean {
    return this.aiThinking;
  }

  /** {@link ChatThreadSource} alias for {@link isAiThinking}. Also true while a
   *  DM/circle peer is typing (CORE-356). */
  isThinking(): boolean {
    return this.aiThinking || this.peerTyping;
  }

  /**
   * The live connection, or a throw. Every write below used to go through
   * `this.conn?.invoke(...)`: when `conn` was null — torn down, or between a
   * terminal close and the scheduled restart — the optional call resolved to
   * `undefined` instead of rejecting. The caller read that as success, cleared
   * the composer, and the message was gone with no error and nothing to retry.
   * A write with nowhere to go is a failure, so say so.
   */
  /**
   * CORE-1020 — вызов не ответил в срок: сокет открыт, но по нему ничего не
   * ходит.
   *
   * Само по себе это состояние не лечится: `onclose` не сработает, потому что
   * соединение формально живо, и всё, что клиент пошлёт дальше, уйдёт в ту же
   * тишину. Поэтому рвём его сами и даём обвязке сделать своё дело — она
   * умеет и перезапуск с отступом, и повторный `WatchChannel`, который дотянет
   * пропущенное (в том числе то самое сообщение, ради которого всё и затевалось:
   * на сервере оно уже есть).
   *
   * Липкость РФ-прокси при этом снимается: путь, на котором туннель встал,
   * рабочим считать нельзя, и следующая попытка должна иметь право пойти
   * прямым адресом.
   */
  /**
   * CORE-1020 — любой вызов хаба под потолком, и все — через одно место.
   *
   * Отправку и ожидание истории обложили дедлайнами по отдельности, а реакция,
   * правка и удаление остались без них: на вставшем туннеле каждый висел вечно.
   * Снаружи это выглядело как «нажал на эмодзи — ничего», «сохранил правку — она
   * не сохранилась и не отменилась». Тот же отказ, та же немота, просто на них
   * никто не смотрел.
   *
   * Условие срыва у всех вызовов одно, поэтому и место одно: не ответивший в
   * срок вызов означает, что сокет мёртв, — путь помечается, соединение рвётся,
   * обвязка перезапускает его сама.
   */
  /**
   * Дождаться, пока сокет снова окажется подключён.
   *
   * Состояние перечитывается у `this.conn` на каждом шаге, а не берётся
   * ссылкой один раз: перезапуск после терминального обрыва строит НОВОЕ
   * соединение, и ожидание на старом объекте не заметило бы возвращения.
   *
   * Разрушенный канал не ждём: там возвращаться нечему.
   */
  private async awaitConnected(deadlineMs: number): Promise<boolean> {
    const until = Date.now() + deadlineMs;
    for (;;) {
      if (this.conn?.state === signalR.HubConnectionState.Connected) return true;
      if (this.destroyed || this.refused) return false;
      if (Date.now() >= until) return false;
      await new Promise((r) => setTimeout(r, RECONNECT_POLL_MS));
    }
  }

  private async call<T>(
    what: string,
    args: unknown[] = [],
    timeoutMs: number = HUB_INVOKE_TIMEOUT_MS,
  ): Promise<T> {
    // Связи нет — это ещё не «нет связи». Переподключение, скорее всего, уже
    // идёт и закончится за секунду; отказать сразу значит сказать человеку
    // неправду о состоянии, которое лечится само.
    if (this.conn?.state !== signalR.HubConnectionState.Connected) {
      await this.awaitConnected(RECONNECT_GRACE_MS);
    }
    try {
      return await withDeadline(
        this.requireConn().invoke<T>(what, ...args),
        timeoutMs,
        what,
      );
    } catch (err) {
      // Рвём соединение ТОЛЬКО на срыве по времени. Остальные отказы — это
      // ответы сервера («модерация не пропустила», «нечего отправлять») или
      // отсутствующий сокет, и связь при них как раз работает: рвать её из-за
      // пустого сообщения значит чинить не то.
      if (err instanceof DeadlineExceededError) this.handleStalledInvoke();
      throw err;
    }
  }

  /**
   * Вызов, которого никто не ждёт: сигнал набора и отметка прочтения.
   *
   * Состояние сокета здесь не проверяется намеренно — эти два ничего не портят,
   * если не дойдут, и исторически звались через `this.conn?.invoke`. Потолок им
   * всё равно нужен: на вставшем туннеле такие обещания копятся с каждой буквой.
   */
  private async callBestEffort(what: string, args: unknown[]): Promise<void> {
    const conn = this.conn;
    if (!conn) return;
    try {
      await withDeadline(conn.invoke(what, ...args), HUB_INVOKE_TIMEOUT_MS, what);
    } catch (err) {
      if (err instanceof DeadlineExceededError) this.handleStalledInvoke();
      throw err;
    }
  }

  /** Путь, по которому сейчас идёт соединение, перестал качать байты. */
  private markPathStalled(): void {
    markHubPathStalled(this.usingProxy);
  }

  private handleStalledInvoke(): void {
    if (this.destroyed) return;
    clearHubProxySticky();
    // Обмены прекратились именно на этом пути — значит следующая попытка не
    // должна выбирать его по умолчанию, иначе перезапуск ходит по кругу.
    this.markPathStalled();
    this.setConnState('disconnected');
    const c = this.conn;
    this.conn = null;
    void c?.stop().catch(() => {});
    this.scheduleRestart();
  }

  private requireConn(): signalR.HubConnection {
    const c = this.conn;
    if (!c || c.state !== signalR.HubConnectionState.Connected) {
      throw new Error('ChatApi hub is not connected');
    }
    return c;
  }

  async send(
    text: string,
    quotedParentId?: string,
    attachments?: any[],
    replyToMessageId?: string,
    replyToFragment?: string,
    clientMessageId?: string,
  ): Promise<void> {
    const parent = quotedParentId ? Number(quotedParentId) : null;
    // CORE-1683 — the answered message rides in its own field: `parentMessageId`
    // is taken by discussion membership (CORE-1654), so a quote inside a
    // discussion had nowhere to go and was dropped at the panel. On the hub it
    // is `replyToMessageId` of `SendMessageV2` (and of the positional
    // `SendReply`, which shipped builds still call — SignalR matches on arity).
    const replyTo = replyToMessageId ? Number(replyToMessageId) : null;
    // `attachmentsJson` — Stream-style descriptors the FE
    // uploaded via POST /api/chat/attachments (CORE-356 T6). Round-trips back
    // through WatchChannel → mapChatApiMessages → AttachmentView.
    const attachmentsJson = attachments && attachments.length ? JSON.stringify(attachments) : null;
    // `pageContextJson` — where the user is in the app, so Agapella can
    // ground the reply. Only for companion/council channels (never peer DMs); the
    // hub stores it on the message as message.page_context and forwards it to the
    // AI. user_id is stamped here rather than tracked in the page module: this is
    // the one place the authenticated id is known for certain.
    const pageContext = isAiCompanionChannelKey(this.channelKey) ? getCurrentPageContext() : undefined;
    const pageContextJson = pageContext
      ? JSON.stringify({ ...pageContext, user_id: this.myId || undefined })
      : null;
    // Отправка на вставшем туннеле — не «не отправилось»: сервер её, скорее
    // всего, принял, и после перезапуска она приедет вместе с историей. Потолок
    // у неё свой: она делает больше работы, чем реакция или отметка прочтения.
    // CORE-1682 — files uploaded with `?channelKey=` arrive as ids: the server
    // keeps the bytes private until this call and builds the descriptor with a
    // signed link itself. `attachmentIds` only exists on `SendMessageV2`, which
    // takes one object; `clientMessageId` makes a retried send idempotent.
    const attachmentIds = (attachments ?? [])
      .map((a) => (a && typeof a === 'object' && a.id != null ? String(a.id) : ''))
      .filter(Boolean);
    // CORE-1749 — a phrase of the quoted message rides only on `SendMessageV2`:
    // `SendReply` has a fixed arity (CORE-615) and no room for it. Without a
    // quoted message there is nothing to take a phrase from, so it is dropped.
    const fragment = replyTo != null ? (replyToFragment ?? '').trim() : '';
    // CORE-1791 (возврат QA 28.10) — и простой текст идёт через `SendMessageV2`:
    // только у него есть ключ повтора. Позиционные `SendMessage`/`SendReply`
    // ключа не знают, и сорванная по времени отправка, которая всё-таки дошла,
    // при повторе давала вторую копию. Ключ приносит композер — один на попытку
    // отправить этот текст; нет ключа — новый, как было у вложений.
    const created: any = await this.call(
      'SendMessageV2',
      [this.channelKey, {
        text,
        parentMessageId: parent,
        replyToMessageId: replyTo,
        ...(attachmentIds.length ? { attachmentIds } : { attachmentsJson }),
        ...(fragment ? { replyToFragment: fragment } : {}),
        clientMessageId: clientMessageId || newClientMessageId(),
        pageContextJson,
      }],
      SEND_TIMEOUT_MS,
    );
    // Some ChatApi deployments broadcast MessageNew only to the OTHER members,
    // not back to the sender — so on DMs the just-sent message never reached
    // this thread (it only showed in the sidebar, which polls REST). If the hub
    // returns the created message DTO, upsert it (deduped by id if MessageNew
    // also fires); otherwise re-pull the channel so our own message still shows.
    if (created && typeof created === 'object' && this.idOf(created)) {
      this.upsert(created);
      this.emit();
    } else {
      await this.watch().catch(() => {});
    }
  }

  /**
   * CORE-1683 — bring the history around `messageId` into the map. The hub's
   * `WatchChannel` only ever hands over the newest 50, so a quote (or a reply
   * notification) pointing further back had nothing on screen to scroll to. The
   * page comes over REST (`?around=`), merges by id — nothing already here is
   * disturbed, live events keep landing — and stays until the next full pull.
   * Inside a discussion the server scopes the page to that discussion, which is
   * exactly the slice `selectDiscussionThread` reads.
   *
   * CORE-1681 (QA 17.10) — «найдено» значит «есть что показать». Удалённое
   * сообщение приходит в истории надгробием с `isDeleted`: в карте оно есть, на
   * экране его нет. Раньше надгробие считалось найденным — переход из
   * уведомления на удалённый ответ молча открывал чат без запроса и без
   * «Сообщение недоступно». Теперь надгробие — это `false`, и в сеть за ним
   * ходить незачем: сервер ответит тем же надгробием (`aroundIsDeleted`).
   */
  async loadAround(messageId: string): Promise<boolean> {
    if (this.messages.has(messageId)) return this.isVisible(messageId);
    const items = await api.getChatChannelMessagesAround(this.channelKey, messageId);
    if (!items.length) return false;
    items.forEach((d) => this.upsert(d));
    this.emit();
    return this.isVisible(messageId);
  }

  /** Сообщение есть в карте и не удалено — то, к чему можно прокрутить. */
  private isVisible(messageId: string): boolean {
    const dto = this.messages.get(messageId);
    return !!dto && !isDeletedChatApiMessage(dto);
  }

  async react(messageId: string, type: string, add: boolean): Promise<void> {
    await this.call(add ? 'AddReaction' : 'RemoveReaction', [Number(messageId), type]);
    // Remember what I just did — the broadcast that follows can't tell me.
    // Recorded only after the invoke resolves, so a failed call leaves the
    // previous state intact. CORE-1479 (возврат QA 23.10) — поправкой к тому,
    // что уже числится моим, а не заменой: снятие одной своей не должно
    // стирать вторую.
    const dto = this.messages.get(messageId);
    const current = dto ? myReactionsOf(dto, this.myReactions) : (this.myReactions.get(messageId) ?? []);
    const rest = current.filter((t) => t !== type);
    this.myReactions.set(messageId, add ? [...rest, type] : rest);
    this.emit();
  }

  async remove(messageId: string): Promise<void> {
    await this.call('DeleteMessage', [Number(messageId)]);
  }

  async edit(messageId: string, text: string): Promise<void> {
    // Same reason as send(): a swallowed edit reported «сохранено» over a dead
    // hub while the original text stayed on screen.
    await this.call('EditMessage', [Number(messageId), text]);
  }

  async typingSignal(): Promise<void> {
    // Сигнал набора никого не ждёт, но и висеть вечно ему незачем: на вставшем
    // туннеле такие обещания копятся с каждой буквой.
    await this.callBestEffort('Typing', [this.channelKey]).catch(() => {});
  }

  /** {@link ChatThreadSource} alias for {@link typingSignal} (slice 5a). */
  notifyTyping(): void {
    void this.typingSignal();
  }

  /** Advance this channel's server read pointer to its newest message.
   *  CORE-629 — announce it so the header/tab unread badges refresh at once
   *  rather than on their next 30 s poll. `MarkRead` has committed by the time
   *  the invoke resolves, so the badge's re-read sees the new state. */
  async markRead(): Promise<void> {
    try {
      await this.callBestEffort('MarkRead', [this.channelKey, null]);
    } catch {
      return; // hub hiccup — leave the pointer; the next markRead retries
    }
    notifyUnreadChanged();
  }

  async disconnect(): Promise<void> {
    this.destroyed = true;
    this.changeCb = null;
    if (this.peerTypingTimer) { clearTimeout(this.peerTypingTimer); this.peerTypingTimer = null; }
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
    if (this.resubscribeTimer) { clearTimeout(this.resubscribeTimer); this.resubscribeTimer = null; }
    const c = this.conn;
    this.conn = null;
    // Let a pending start() settle first, else SignalR logs a start/stop race.
    await this.startPromise?.catch(() => {});
    await c?.stop().catch(() => {});
  }
}
